import { P2GAlert, P2GBox, P2GGrid, P2GLink, P2GLinkGroup, P2GText } from '@parcel2go/core-ui';
import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import RequirementsField from '~/components/core/forms/form-field-with-named-requirement';
import { TranslationNamespaces } from '~/lib/constants';
import { Currency, QuoteParcel, QuoteRequirementSection, Restrictions } from '~/lib/types/basket-types';
import ContentsSummary from './parcel-contents';
import RequirementsFieldDebounced from '~/components/core/forms/form-field-with-named-requirement-debounced';
import { v4 as uuidv4 } from 'uuid';
import { displayCurrencySymbol, toSafeMinorCurrency } from '~/lib/prices';
import { StandardFormWidths } from '~/lib/forms';

type ParcelProps = {
  parcel: QuoteParcel;
  onChange: (parcel: QuoteParcel) => void;
  onRemove: (parcel: QuoteParcel) => void;
  onDuplicate: (parcel: QuoteParcel) => void;
  onExpand: () => void;
  loading: boolean;
  restrictions: Restrictions;
  requirements: QuoteRequirementSection;
  errors: Record<string, string>;
  removeable: boolean;
  index: number;
  currency: Currency;
  requiresDimensions: boolean;
};

export default function Parcel({
  index,
  parcel,
  onChange,
  onRemove,
  onDuplicate,
  loading,
  errors,
  removeable,
  requirements,
  currency,
  requiresDimensions,
  onExpand
}: ParcelProps) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const [valueKey, setValueKey] = useState(uuidv4());

  const value = useMemo(() => {
    if (parcel.value === undefined || parcel.value === null) {
      return '';
    }
    return (parcel.value / 100).toString();
  }, [parcel]);

  const onUpdateValue = useCallback(
    (value: string | undefined) => {
      if (value === undefined || value === null || value.length === 0) {
        onChange({ ...parcel, value: undefined });
        return;
      }
      onChange({ ...parcel, value: toSafeMinorCurrency(value) });
    },
    [parcel, onChange]
  );

  const onContentChange = useCallback(
    (p: QuoteParcel) => {
      onChange(p);
      // This resets the parcel value ui debounce.
      if (p.value != parcel.value) {
        setValueKey(uuidv4());
      }
    },
    [onChange, setValueKey, parcel.value]
  );

  const links = useMemo(() => {
    const output = [
      <P2GLink
        onClick={() => onDuplicate(parcel)}
        dataTestId={`btn-duplicate-parcel-${index}`}
        key={`btn-duplicate-parcel-${index}`}
      >
        {t('PARCEL_DUPLICATE_BUTTON', {
          defaultValue: 'Duplicate'
        })}
      </P2GLink>
    ];

    if (removeable) {
      output.push(
        <P2GLink
          onClick={() => onRemove(parcel)}
          dataTestId={`btn-delete-parcel-${index}`}
          key={`btn-delete-parcel-${index}`}
        >
          {t('PARCEL_DELETE_BUTTON', {
            defaultValue: 'Delete'
          })}
        </P2GLink>
      );
    }

    return output;
  }, [removeable, t, onDuplicate, onRemove, parcel, index]);

  return (
    <>
      <P2GGrid container spacing={4} dataTestId={`parcel-${index}`}>
        <P2GGrid item xs={12} {...StandardFormWidths}>
          <P2GGrid container spacing={2}>
            <P2GGrid item xs={12}>
              <ContentsSummary
                translationKey="PARCEL_FIELD_CONTENTS_LABEL"
                index={index}
                parcel={parcel}
                onChange={onContentChange}
                errors={errors}
                loading={loading}
                requirements={requirements}
              />
            </P2GGrid>

            <P2GGrid item xs={requiresDimensions ? 12 : 6}>
              <RequirementsFieldDebounced
                key={valueKey}
                defaultLabel={t('PARCEL_FIELD_VALUE_LABEL', { defaultValue: 'Contents Value' })}
                dataTestId={`txt-parcel-${index}-value`}
                id={`txt-${parcel.id}-value`}
                requirements={requirements}
                translationKey="PARCEL_FIELD_VALUE_LABEL"
                name="value"
                data={value}
                update={onUpdateValue}
                loading={loading}
                errors={errors}
                startAdornment={displayCurrencySymbol(currency)}
                defaultStep={0.01}
                defaultMinValue={0.01}
                defaultRequired={true}
                forceType="decimal"
                unmasked
              />
            </P2GGrid>

            <P2GGrid item xs={6}>
              <RequirementsFieldDebounced
                defaultLabel={t('PARCEL_FIELD_WEIGHT_LABEL', { defaultValue: 'Weight' })}
                dataTestId={`txt-parcel-${index}-weight`}
                id={`txt-${parcel.id}-weight`}
                requirements={requirements}
                translationKey="PARCEL_FIELD_WEIGHT_LABEL"
                name="weight"
                data={parcel?.weight?.toString() || ''}
                update={(e) => onChange({ ...parcel, weight: parseFloat(e) })}
                loading={loading}
                errors={errors}
                unitEnd
                defaultMinValue={0.01}
                defaultStep={0.01}
                defaultRequired={true}
                unmasked
              />
            </P2GGrid>

            {requiresDimensions && (
              <>
                <P2GGrid item xs={6}>
                  <RequirementsField
                    defaultLabel={t('PARCEL_FIELD_LENGTH_LABEL', { defaultValue: 'Length' })}
                    dataTestId={`txt-parcel-${index}-length`}
                    id={`txt-${parcel.id}-length`}
                    requirements={requirements}
                    translationKey="PARCEL_FIELD_LENGTH_LABEL"
                    name="length"
                    data={parcel?.length?.toString() || ''}
                    update={(e) => onChange({ ...parcel, length: parseInt(e) })}
                    loading={loading}
                    errors={errors}
                    unitEnd
                    defaultMinValue={1}
                    defaultStep={1}
                    forcedRequired={true}
                    unmasked
                  />
                </P2GGrid>

                <P2GGrid item xs={6}>
                  <RequirementsField
                    defaultLabel={t('PARCEL_FIELD_WIDTH_LABEL', { defaultValue: 'Width' })}
                    dataTestId={`txt-parcel-${index}-width`}
                    id={`txt-${parcel.id}-width`}
                    requirements={requirements}
                    translationKey="PARCEL_FIELD_WIDTH_LABEL"
                    name="width"
                    data={parcel?.width?.toString() || ''}
                    update={(e) => onChange({ ...parcel, width: parseInt(e) })}
                    loading={loading}
                    errors={errors}
                    unitEnd
                    defaultMinValue={1}
                    defaultStep={1}
                    forcedRequired={true}
                    unmasked
                  />
                </P2GGrid>

                <P2GGrid item xs={6}>
                  <RequirementsField
                    defaultLabel={t('PARCEL_FIELD_HEIGHT_LABEL', { defaultValue: 'Height' })}
                    dataTestId={`txt-parcel-${index}-height`}
                    id={`txt-${parcel.id}-height`}
                    requirements={requirements}
                    translationKey="PARCEL_FIELD_HEIGHT_LABEL"
                    name="height"
                    data={parcel?.height?.toString() || ''}
                    update={(e) => onChange({ ...parcel, height: parseInt(e) })}
                    loading={loading}
                    errors={errors}
                    unitEnd
                    defaultMinValue={1}
                    defaultStep={1}
                    forcedRequired={true}
                    unmasked
                  />
                </P2GGrid>
              </>
            )}
            <P2GGrid item xs={12}>
              <P2GLinkGroup links={links} />
            </P2GGrid>
          </P2GGrid>
        </P2GGrid>
      </P2GGrid>
      {!requiresDimensions && (
        <P2GBox mt={2}>
          <P2GAlert type="info">
            <P2GText variant="body1">
              <Trans
                i18nKey="PARCEL_LONGER_THAN_1M_MESSAGE"
                t={t}
                defaults="If your item is longer than 1m, please <0>click here</0> and enter the dimensions."
                components={[<P2GLink onClick={onExpand} />]}
              />
            </P2GText>
          </P2GAlert>
        </P2GBox>
      )}
    </>
  );
}
